import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import allImages from "../../../assets/images-import";

import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { InputField } from "../../Common/FormElements/InputField";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { ImageInputField } from "../../Common/FormElements/ImageInputField";

import { fetchProductById } from "../../../services/productService";
import {
  createProductVariant,
  updateProductVariant,
  fetchProductVariantById,
} from "../../../services/productVariantService";

export const AddProductVariant = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedProductVariant,
  setEditMode,
}) => {
  const { productId } = useParams();
  const [imageUrl, setImageUrl] = useState(allImages.ImagePlaceholder);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [productName, setproductName] = useState("");
  const navigate = useNavigate();
  const PageLevelAccessurl = "product/:productId/variant";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const discountRef = useRef(null);
  const actualRef = useRef(null);
  const productSizeRef = useRef(null);
  const maxQuantityRef = useRef(null);
  const productImageFileRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    ProductId: productId,
    ColorId: 0,
    ProductSize: "",
    DiscountPrice: 0,
    ActualPrice: 0,
    MaxQuantity: 100,
    BestSeller: false,
    InStock: false,
    Featured: false,
    Published: false,
    ProductImageFile: null,
  });

  // ✅ Register all fields
  useEffect(() => {
    registerField("DiscountPrice", discountRef, "Discount Price");
    registerField("ActualPrice", actualRef, "Actual Price");
    registerField("ProductSize", productSizeRef, "Product Size");
    registerField("MaxQuantity", maxQuantityRef, "Product Size");
    registerField("ProductImageFile", productImageFileRef, "Thumb Image");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchProductById(productId);
        if (response && response.isSuccess && response.result) {
          setproductName(response.result.productName);
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  });

  useEffect(() => {
    const fetchData = async () => {
      if (editMode && initialData.id) {
        try {
          const data = await fetchProductVariantById(initialData.id);
          if (data && data.isSuccess && data.result) {
            setFormValues({
              ProductId: data.result.productId,
              ColorId: data.result.colorId,
              DiscountPrice: data.result.discountPrice,
              ActualPrice: data.result.actualPrice,
              ProductSize: data.result.productSize,
              MaxQuantity: data.result.maxQuantity,
              BestSeller: data.result.bestSeller,
              InStock: data.result.inStock,
              Featured: data.result.featured,
              Published: data.result.published,
            });
            if (data.result.productImage && data.result.productImage !== "") {
              if (imageUrl && imageUrl.startsWith("blob:")) {
                URL.revokeObjectURL(imageUrl);
              }
              setImageUrl(data.result.productImage);
            } else {
              setImageUrl(allImages.placeholder);
            }
          }
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormValues({
          ProductId: productId,
          ColorId: 0,
          ProductSize: "",
          DiscountPrice: 0,
          ActualPrice: 0,
          MaxQuantity: 100,
          BestSeller: false,
          InStock: false,
          Featured: false,
          Published: false,
          ProductImageFile: null,
        });
      }
    };
    fetchData();
  }, [editMode, initialData, productId, imageUrl]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };
      // validation: discount should not exceed actual
      if (id === "DiscountPrice" || id === "ActualPrice") {
        const actual = parseFloat(newValues.ActualPrice || 0);
        const discount = parseFloat(newValues.DiscountPrice || 0);

        if (discount > actual) {
          toast.error("Discount Price cannot be greater than Actual Price");
          newValues.DiscountPrice = ""; // reset or keep old value
        }
      }
      return newValues;
    });
  };
  // ✅ Fix file handling with cleanup
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, ProductImageFile: file }));
    }
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      discountRef,
      actualRef,
      maxQuantityRef,
      productSizeRef,
      productImageFileRef,
    };
    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ✅ Touch all fields to show validation errors
      touchAllFields();

      // ✅ Validate all fields using refs
      const isValid = validateAllFields();

      if (!isValid) {
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      try {
        if (editMode) {
          setIsButtonDisabled(true);
          var updateResponseData = await updateProductVariant({
            ...formValues,
            Id: initialData.id,
          });
          // ✅ Clear form errors
          clearErrors();
          if (updateResponseData && updateResponseData?.isSuccess) {
            toast.success(updateResponseData?.statusMessage);
          } else {
            toast.error(updateResponseData?.statusMessage);
          }
        } else {
          setIsButtonDisabled(true);
          var createResponseData = await createProductVariant(formValues);
          resetAllFields();
          setFormValues({
            ProductId: productId,
            ColorId: 0,
            ProductSize: "",
            DiscountPrice: 0,
            ActualPrice: 0,
            MaxQuantity: 100,
            BestSeller: false,
            InStock: false,
            Featured: false,
            Published: false,
            ProductImageFile: null,
          });
          if (imageUrl && imageUrl.startsWith("blob:")) {
            URL.revokeObjectURL(imageUrl);
          }
          setImageUrl(allImages.ImagePlaceholder);
          // ✅ Clear form errors
          clearErrors();
          if (createResponseData?.isSuccess) {
            toast.success(createResponseData?.statusMessage);
          } else {
            toast.error(createResponseData?.statusMessage);
          }
        }
        if (onSuccess) onSuccess();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formValues, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    setFormValues({
      ProductId: productId,
      ColorId: 0,
      ProductSize: "",
      DiscountPrice: 0,
      ActualPrice: 0,
      MaxQuantity: 100,
      BestSeller: false,
      InStock: false,
      Featured: false,
      Published: false,
      ProductImageFile: null,
    });
    resetAllFields();
    setSelectedProductVariant(null);
    setEditMode(false);
    if (imageUrl && imageUrl.startsWith("blob:")) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(allImages.placeholder);
  };

  return (
    <>
      <ComponentHeader title="Manage Product Variant" />
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-8">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  {editMode ? "Update Product Variant" : "Add Product Variant"}
                </h5>

                <span className=" badge bg-primary fs-12">
                  {productName
                    ? `Product: ${productName}`
                    : "No Product Selected"}{" "}
                </span>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={productSizeRef}
                      label="Product Size"
                      id="ProductSize"
                      value={formValues.ProductSize}
                      placeholder="Enter your Product Size"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={300}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={actualRef}
                      label="Actual Price"
                      id="ActualPrice"
                      value={formValues.ActualPrice}
                      placeholder="Enter your Actual Price"
                      onChange={handleInputChange}
                      required={true}
                      validationType="decimal"
                      maxLength={10}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={discountRef}
                      label="Discount Price"
                      id="DiscountPrice"
                      value={formValues.DiscountPrice}
                      placeholder="Enter your Discount Price"
                      onChange={handleInputChange}
                      required={true}
                      validationType="decimal"
                      maxLength={10}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={maxQuantityRef}
                      label="Max Quantity"
                      id="MaxQuantity"
                      value={formValues.MaxQuantity}
                      placeholder="Enter your Max Quantity"
                      onChange={handleInputChange}
                      required={true}
                      validationType="numberonly"
                      maxLength={10}
                      showError={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Thumb Image</h5>
              </div>
              <div className="card-body p-4">
                <ImageInputField
                  ref={productImageFileRef}
                  id="ProductImageFile"
                  imageUrl={imageUrl}
                  onFileChange={handleFileChange}
                  required={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-4">
            <div className="row d-flex justify-content-between align-items-center">
              <div className="col-lg-9">
                <div className="row">
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <div className="d-flex align-items-center">
                      <label
                        htmlFor="Published"
                        className="form-label pe-3 cursor-pointer"
                      >
                        Published Variant
                      </label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="Published"
                          name="Published"
                          checked={formValues.Published}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="Published">
                          {formValues.Published ? "Yes" : "No"}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <div className="d-flex align-items-center">
                      <label
                        htmlFor="Featured"
                        className="form-label pe-3 cursor-pointer"
                      >
                        Featured Variant
                      </label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="Featured"
                          name="Featured"
                          checked={formValues.Featured}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="Featured">
                          {formValues.Featured ? "Yes" : "No"}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <div className="d-flex align-items-center">
                      <label
                        htmlFor="BestSeller"
                        className="form-label pe-3 cursor-pointer"
                      >
                        BestSeller Variant
                      </label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="BestSeller"
                          name="BestSeller"
                          checked={formValues.BestSeller}
                          onChange={handleInputChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="BestSeller"
                        >
                          {formValues.BestSeller ? "Yes" : "No"}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <div className="d-flex align-items-center">
                      <label
                        htmlFor="InStock"
                        className="form-label pe-3 cursor-pointer"
                      >
                        Variant InStock
                      </label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="InStock"
                          name="InStock"
                          checked={formValues.InStock}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="InStock">
                          {formValues.InStock ? "Yes" : "No"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3">
                <div>
                  <button
                    type="submit"
                    className="btn btn-secondary pt-1 pb-1 p-3"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled
                      ? editMode
                        ? "Updating"
                        : "Saving"
                      : editMode
                      ? "Update"
                      : "Save"}
                  </button>
                  {editMode && (
                    <button
                      type="button"
                      onClick={handleAddNewClick}
                      className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
